/**
 * LanguageSwitcher - the single canonical language switcher for the app.
 *
 * Dropdown select, re-skinned with theme tokens (instead of hardcoded colors)
 * so it fits both the legacy sidebar and the dashboard topbar control row.
 * Drives the shared react-i18next instance, so changing it updates every consumer.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Select, MenuItem, FormControl, InputLabel } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useApi } from '@backstage/core-plugin-api';
import { toastApiRef } from '@backstage/frontend-plugin-api';
import Tooltip from '@material-ui/core/Tooltip';
import TranslateIcon from '@material-ui/icons/Translate';
import { useControlStyles } from '../shell/useControlStyles';
import { queueLanguageChange } from '../../i18n/languageChangeQueue';

const useStyles = makeStyles(theme => ({
  formControl: {
    minWidth: 120,
    maxWidth: 200,
    '& .MuiInputLabel-root': {
      color: theme.palette.text.secondary,
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: theme.palette.primary.main,
    },
    '& .MuiOutlinedInput-root': {
      color: theme.palette.text.primary,
      '& fieldset': {
        borderColor: theme.palette.divider,
      },
      '&:hover fieldset': {
        borderColor: theme.palette.action.hover,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
    '& .MuiSelect-icon': {
      color: theme.palette.text.secondary,
    },
  },
}));

// Languages the app ships. Codes must match the i18n resources.
const LANGUAGES = [
  { code: 'en', label: 'EN', full: 'English' },
  { code: 'ja', label: '日本語', full: '日本語' },
];

type Props = {
  className?: string;
  showChangeNotification?: boolean;
};

type LanguageController = {
  currentLanguage: string;
  changeLanguage: (newLanguage: string) => Promise<void>;
};

const useLanguageController = (
  showChangeNotification = true,
): LanguageController => {
  const { i18n, t } = useTranslation();
  const toastApi = useApi(toastApiRef);
  const [currentLanguage, setCurrentLanguage] = React.useState(i18n.language);

  React.useEffect(() => {
    const onChange = (lng: string) => setCurrentLanguage(lng);
    i18n.on('languageChanged', onChange);
    return () => {
      i18n.off('languageChanged', onChange);
    };
  }, [i18n]);

  const changeLanguage = async (newLanguage: string) => {
    try {
      await queueLanguageChange(newLanguage);

      if (showChangeNotification) {
        const languageLabel =
          LANGUAGES.find(lang => lang.code === newLanguage)?.label ||
          newLanguage;

        toastApi.post({
          title: t('common:messages.languageChanged', {
            language: languageLabel,
          }),
          status: 'success',
          timeout: 5000,
        });
      }

      // TODO (CB25SES-921): Sync language preference to backend API for cross-device persistence
      // Once backend implements the UpdateUserLanguage endpoint, call it here:
      // try {
      //   await fetch('/api/users/me/language', {
      //     method: 'PATCH',
      //     headers: { 'Content-Type': 'application/json' },
      //     body: JSON.stringify({ language: newLanguage }),
      //   });
      // } catch (error) {
      //   console.warn('Failed to sync language preference to backend:', error);
      // }
    } catch (error) {
      toastApi.post({
        title:
          t('common:messages.languageChangeFailed') ||
          'Failed to change language',
        status: 'danger',
        timeout: 5000,
      });
      // eslint-disable-next-line no-console
      console.error('Failed to change language:', error);
    }
  };

  return { currentLanguage, changeLanguage };
};

// Full language dropdown used where all available languages should be visible.
export const LanguageSwitcher: React.FC<Props> = ({
  className,
  showChangeNotification = true,
}) => {
  const classes = useStyles();
  const { currentLanguage, changeLanguage } = useLanguageController(
    showChangeNotification,
  );
  const { t } = useTranslation();

  const handleLanguageChange = (event: React.ChangeEvent<{ value: unknown }>) =>
    changeLanguage(event.target.value as string);

  return (
    <FormControl
      className={`${classes.formControl} ${className ?? ''}`}
      variant="outlined"
      size="small"
    >
      <InputLabel id="language-switcher-label">
        {t('labels.language')}
      </InputLabel>
      <Select
        labelId="language-switcher-label"
        id="language-switcher"
        inputProps={{ 'data-testid': 'language-switcher-input' }}
        value={currentLanguage}
        onChange={handleLanguageChange}
        label={t('labels.language')}
      >
        {LANGUAGES.map(lang => (
          <MenuItem key={lang.code} value={lang.code}>
            {lang.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

type ToggleProps = Props & {
  iconOnly?: boolean;
};

// Compact language toggle used in the shell; clicking cycles to the next language.
export const LanguageSwitcherToggle: React.FC<ToggleProps> = ({
  className,
  iconOnly = false,
}) => {
  const ctrl = useControlStyles();
  const { currentLanguage, changeLanguage } = useLanguageController();
  const currentIndex = Math.max(
    0,
    LANGUAGES.findIndex(language => currentLanguage?.startsWith(language.code)),
  );
  const current = LANGUAGES[currentIndex] ?? LANGUAGES[0];
  const next = LANGUAGES[(currentIndex + 1) % LANGUAGES.length];

  return (
    <Tooltip title={`Switch to ${next.full}`}>
      <button
        type="button"
        className={`${ctrl.control} ${className ?? ''}`}
        aria-label={`Language: ${current.full}. Switch to ${next.full}`}
        onClick={() => changeLanguage(next.code)}
      >
        <TranslateIcon className={ctrl.icon} />
        {!iconOnly && current.label}
      </button>
    </Tooltip>
  );
};
